import { getKey, hasKey, setKey } from './cache';

const inflight = new Map();

const DEFAULT_INFLIGHT_TIMEOUT_MS = 15_000;

const MAX_INFLIGHT = 500;

const withTimeout = (promise, timeoutMs, onTimeout) =>
  new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      if (typeof onTimeout === 'function') {
        onTimeout();
      }
      reject(new Error(`SERVER_CACHE_TIMEOUT_${timeoutMs}ms`));
    }, timeoutMs);

    promise.then(
      value => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(value);
      },
      error => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(error);
      }
    );
  });

export const fetchWithServerCache = async (key, fetcher, options = {}) => {
  if (typeof window !== 'undefined') {
    return fetcher();
  }

  const cached = getKey(key);
  if (cached !== undefined || hasKey(key)) {
    return cached;
  }

  if (inflight.has(key)) {
    return inflight.get(key);
  }

  if (inflight.size >= MAX_INFLIGHT) {
    throw new Error(`SERVER_CACHE_INFLIGHT_LIMIT_${MAX_INFLIGHT}`);
  }

  const { timeoutMs = DEFAULT_INFLIGHT_TIMEOUT_MS } = options;

  const fetchPromise = (async () => {
    try {
      const result = await withTimeout(
        Promise.resolve().then(fetcher),
        timeoutMs,
        () => inflight.delete(key)
      );
      setKey(key, result);
      return result;
    } catch (error) {
      inflight.delete(key);
      throw error;
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, fetchPromise);
  return fetchPromise;
};

export const getInflightSize = () => inflight.size;

export const listInflightKeys = (limit = 10) =>
  Array.from(inflight.keys()).slice(0, limit);
